// web/src/pages/statements/StatementRunsListPage.tsx
//
// Statement runs list (Plan IV Task 6).
//
// Canonical chrome:
// - ListPage shell with title + itemCount + primary action ("New statement run").
// - DataGrid with built-in pagination wired to the server.
//
// No search/filterChips slots: the legacy view had none, and Plan IV Task 6 is
// a chrome-swap only — no new server params. DataGrid's pageIndex is 0-based;
// the backend `page` query param is 1-based, so we translate at the boundary.

import { useMemo, useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";

import { listStatementRuns } from "@/lib/invoices-payments";
import type { BulkStatementRunSummary } from "@/lib/statement-runs";
import { BulkRunStatusBadge } from "@/components/invoices-payments";
import { Button } from "@/components/ui/button";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn, type SortDirection } from "@/components/data-grid";

const PAGE_SIZE = 25;

export default function StatementRunsListPage() {
  // DataGrid pageIndex is 0-based; backend `page` is 1-based.
  const [pageIndex, setPageIndex] = useState(0);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["statement-runs", pageIndex, PAGE_SIZE],
    queryFn: () => listStatementRuns({ page: pageIndex + 1, pageSize: PAGE_SIZE }),
    retry: false,
  });

  // Sort state — client-side only; backend (listStatementRuns) has no `sort`
  // param. Sort applies to the current page's rows, not the full result set.
  const [sort, setSort] = useState<{ columnId: string; direction: SortDirection } | null>(null);

  const visibleRows = useMemo(() => {
    const rows = data?.items ?? [];
    if (!sort) return rows;
    const dir = sort.direction === "asc" ? 1 : -1;
    const valueFor = (r: BulkStatementRunSummary): string | number | null => {
      switch (sort.columnId) {
        case "started":     return r.startedAtUtc;
        case "completed":   return r.completedAtUtc ?? null;
        case "requestedBy": return r.requestedByUserId ?? null;
        case "mode":        return r.deliveryMode ?? null;
        case "accounts":    return r.accountCount ?? null;
        case "status":      return r.status ?? null;
        default:            return null;
      }
    };
    return [...rows].sort((a, b) => {
      const va = valueFor(a);
      const vb = valueFor(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [data, sort]);

  const cols: DataGridColumn<BulkStatementRunSummary>[] = [
    {
      id: "started",
      header: "Started",
      sortable: true,
      accessor: (r) => new Date(r.startedAtUtc).toLocaleString(),
    },
    {
      id: "completed",
      header: "Completed",
      sortable: true,
      accessor: (r) => (r.completedAtUtc ? new Date(r.completedAtUtc).toLocaleString() : "—"),
    },
    {
      id: "requestedBy",
      header: "Requested by",
      sortable: true,
      accessor: (r) => (
        <>
          {r.requestedByUserId}
          {r.isRecurring && <span className="ml-1 text-xs text-slate-400">(auto)</span>}
        </>
      ),
    },
    { id: "mode",     header: "Mode",     sortable: true, accessor: (r) => r.deliveryMode },
    { id: "accounts", header: "Accounts", sortable: true, accessor: (r) => r.accountCount },
    {
      id: "okFail",
      header: "OK / Fail",
      accessor: (r) => `${r.successCount} / ${r.failureCount}`,
    },
    {
      id: "status",
      header: "Status",
      sortable: true,
      accessor: (r) => <BulkRunStatusBadge status={r.status} />,
    },
    {
      id: "actions",
      header: "",
      accessor: (r) => (
        <Link to={`/statements/runs/${r.runId}`} className="text-blue-600 hover:underline">
          Open
        </Link>
      ),
    },
  ];

  return (
    <ListPage
      title="Statement runs"
      primaryAction={
        <Button asChild>
          <Link to="/statements/runs/new">New statement run</Link>
        </Button>
      }
    >
      <div data-testid="statement-runs-list">
        {isError ? (
          <div role="alert" className="text-red-600">
            Could not load runs. Retry?
          </div>
        ) : (
          <DataGrid
            rows={visibleRows}
            columns={cols}
            rowKey={(r) => r.runId}
            totalRows={data?.totalCount ?? 0}
            pageIndex={pageIndex}
            pageSize={PAGE_SIZE}
            loading={isLoading}
            emptyHeadline="No statement runs yet. Click 'New statement run' to start one."
            onPageChange={setPageIndex}
            onSortChange={setSort}
          />
        )}
      </div>
    </ListPage>
  );
}
